import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Avatar from '../components/Avatar';
import AudioAvatarPlayer from '../components/AudioAvatarPlayer';
import { Button } from '../components/ui/button';
import { Grid3x3, UserSquare2, Camera, Film, Volume2, Play, Radio, Bookmark, CalendarClock, Lock, BookOpen } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import api from '../api/client';
import { fetchUserPosts, mediaUrl } from '../api/posts';
import { livesByUser } from '../api/lives';
import { toggleFollow } from '../api/social';
import useAppConfig from '../hooks/useAppConfig';
import { blockUser, unblockUser, getBlockStatus } from '../api/blocks';
import CallCreatorSection from '../components/o2ocall/CallCreatorSection';
import { langFlag, langLabel, MODE_LABELS, fmtAvgDuration, fmtRelativeTime, fmtMemberSince } from '../lib/creatorMeta';
import LiveCard from '../components/LiveCard';
import VerifiedBadge from '../components/VerifiedBadge';
import FollowListDialog from '../components/FollowListDialog';
import BackButton from '../components/BackButton';
import VetrinaProfile from '../components/vetrina/VetrinaProfile';
import { ProfileShareMenuItems } from '../components/ProfileShareMenuItems';
import { ProfileLiveSchedule } from '../components/ProfileLiveSchedule';
import { Shield, ShieldOff, MoreHorizontal, ShieldAlert } from 'lucide-react';

const Stat = ({ value, label, onClick, testId }) => {
  const inner = (
    <>
      <span className="text-base font-semibold md:text-base">{(value || 0).toLocaleString()}</span>
      <span className="text-sm text-foreground">{label}</span>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="flex flex-col items-center hover:opacity-70 md:flex-row md:items-baseline md:gap-1.5"
        data-testid={testId}
      >
        {inner}
      </button>
    );
  }
  return (
    <div className="flex flex-col items-center md:flex-row md:items-baseline md:gap-1.5" data-testid={testId}>
      {inner}
    </div>
  );
};

const PostThumb = ({ post, onClick }) => {
  const url = mediaUrl(post.media_url);
  const locked = post.is_paid && !post.has_access;
  return (
    <button
      onClick={onClick}
      className="group relative aspect-square overflow-hidden bg-secondary"
    >
      {!locked && !blockStatus.i_blocked_them && <ProfileLiveSchedule key={profile.id} lives={lives} />}
      {locked ? (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1 bg-gradient-to-br from-violet-200 to-fuchsia-200 text-violet-900">
          <Volume2 className="h-7 w-7" />
          <span className="text-[10px] font-bold uppercase">Locked</span>
          <span className="text-[10px]">{post.price} cr</span>
        </div>
      ) : (
        <>
          {post.media_type === 'image' && (
            <img src={url} alt="" className="h-full w-full object-cover" loading="lazy" />
          )}
          {post.media_type === 'video' && (
            <>
              <video src={url} className="h-full w-full object-cover" muted preload="metadata" />
              <div className="absolute right-2 top-2 text-white drop-shadow">
                <Film className="h-4 w-4" />
              </div>
            </>
          )}
          {post.media_type === 'audio' && (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-neutral-200 to-neutral-300">
              <Volume2 className="h-10 w-10 text-foreground" strokeWidth={1.5} />
            </div>
          )}
          {post.media_type === 'text' && (
            <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 bg-gradient-to-br from-neutral-200 to-neutral-300 px-2">
              <BookOpen className="h-8 w-8 text-foreground" strokeWidth={1.5} />
              {post.title && <span className="line-clamp-2 text-center text-[10px] font-semibold">{post.title}</span>}
            </div>
          )}
        </>
      )}
      <div className="absolute inset-0 hidden items-center justify-center bg-black/30 text-white group-hover:flex">
        <Play className="h-7 w-7" />
      </div>
    </button>
  );
};

const UserProfilePage = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const { toast } = useToast();
  // Phase F2 · Voice-First: hide social metrics + real name in public UI
  const { peopleFeedEnabled } = useAppConfig();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('posts');
  const [posts, setPosts] = useState([]);
  const [lives, setLives] = useState([]);
  const [loadingTab, setLoadingTab] = useState(true);
  // Phase 27.8 — block state.
  const [blockStatus, setBlockStatus] = useState({ i_blocked_them: false, they_blocked_me: false });
  const [blockBusy, setBlockBusy] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [followDlg, setFollowDlg] = useState(null); // null | 'followers' | 'following'
  // Private Profile Phase 2 — access status for private creators.
  const [accessInfo, setAccessInfo] = useState(null);
  const [accessBusy, setAccessBusy] = useState(false);

  const isOwnProfile = me && username && me.username === username.toLowerCase();

  useEffect(() => {
    if (isOwnProfile || !username) return;
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/users/${username}/profile-access`);
        if (alive) setAccessInfo(r.data);
      } catch (_e) { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [username, isOwnProfile, me]);

  useEffect(() => {
    if (isOwnProfile) return;
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const res = await api.get(`/users/${username}`);
        if (alive) setProfile(res.data);
      } catch (e) {
        if (alive) {
          toast({ title: 'User not found', variant: 'destructive' });
          navigate('/');
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, isOwnProfile]);

  // Phase 27.8 — fetch block status (only when viewing someone else's profile).
  useEffect(() => {
    if (isOwnProfile || !me || !username) return;
    let alive = true;
    (async () => {
      try {
        const d = await getBlockStatus(username);
        if (alive) setBlockStatus(d);
      } catch (_e) { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [username, me, isOwnProfile]);

  const handleToggleBlock = async () => {
    if (blockBusy) return;
    setBlockBusy(true);
    try {
      if (blockStatus.i_blocked_them) {
        await unblockUser(username);
        toast({ title: `@${username} sbloccato` });
        setBlockStatus({ ...blockStatus, i_blocked_them: false });
      } else {
        const confirmed = window.confirm(
          `Vuoi bloccare @${username}?\n\n` +
          `Non potrà più seguirti, scriverti, commentare i tuoi post, ` +
          `chiederti di partecipare alle tue live. ` +
          `Anche tu non potrai interagire con il suo profilo.`,
        );
        if (!confirmed) {
          setBlockBusy(false);
          return;
        }
        await blockUser(username);
        toast({
          title: `@${username} bloccato`,
          description: 'Non potrà più interagire con te. Puoi sbloccarlo in qualsiasi momento da Impostazioni.',
        });
        setBlockStatus({ ...blockStatus, i_blocked_them: true });
      }
    } catch (e) {
      toast({
        title: 'Operazione non riuscita',
        description: e.response?.data?.detail?.split('|').pop() || '',
        variant: 'destructive',
      });
    } finally {
      setBlockBusy(false);
      setShowActions(false);
    }
  };

  useEffect(() => {
    if (isOwnProfile) return;
    if (!username) return;
    let alive = true;
    setLoadingTab(true);
    const fetchAll = async () => {
      try {
        const [p, l] = await Promise.all([
          fetchUserPosts(username),
          livesByUser(username),
        ]);
        if (alive) {
          setPosts(p);
          setLives(l);
        }
      } catch (e) {
        if (alive) {
          setPosts([]);
          setLives([]);
        }
      } finally {
        if (alive) setLoadingTab(false);
      }
    };
    fetchAll();
    // Phase 26 — keep the profile lives strip in sync with the rest of
    // the app: refresh on data-changed events + every 30s.
    const onChanged = () => { fetchAll(); };
    window.addEventListener('nymia:data-changed', onChanged);
    const t = setInterval(fetchAll, 30000);
    return () => {
      alive = false;
      window.removeEventListener('nymia:data-changed', onChanged);
      clearInterval(t);
    };
  }, [username, isOwnProfile]);

  // Redirect to /profile if viewing own profile (after hooks)
  if (isOwnProfile) {
    return <Navigate to="/profile" replace />;
  }

  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <div className="h-7 w-7 animate-spin rounded-full border-2 border-border border-t-neutral-800" />
      </div>
    );
  }
  if (!profile) return null;

  // Phase 27.8 — if the OTHER user blocked me, hide all interactive content.
  if (blockStatus.they_blocked_me) {
    return (
      <div className="mx-auto max-w-md px-4 py-20 text-center" data-testid="profile-blocked-banner">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-500/10">
          <ShieldAlert className="h-8 w-8 text-red-500" />
        </div>
        <h1 className="text-2xl font-bold">Accesso non disponibile</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          @{profile.username} ti ha bloccato. Non puoi vedere il profilo,
          inviare messaggi o partecipare alle sue live.
        </p>
        <Button onClick={() => navigate('/')} className="mt-6" data-testid="profile-blocked-home-btn">
          Torna alla home
        </Button>
      </div>
    );
  }

  const upcomingLives = lives.filter((l) => l.status === 'scheduled' || l.status === 'live');
  const pastLives = lives.filter((l) => l.status === 'ended');
  const scheduledLives = lives.filter((l) => l.status === 'scheduled');
  const liveNow = lives.find((l) => l.status === 'live');

  // Private Profile Phase 2 — content is locked for non-approved viewers.
  const locked = !!profile.is_private
    && accessInfo?.my_status !== 'approved'
    && accessInfo?.my_status !== 'self';
  const accessPrice = accessInfo?.price_credits || 0;

  const handleRequestAccess = async () => {
    if (!me) { navigate('/login'); return; }
    if (accessBusy) return;
    if (accessPrice > 0) {
      const ok = window.confirm(
        `Richiedere l'accesso a @${profile.username} per ${accessPrice} Crediti?\n\n` +
        `I crediti restano in garanzia finché il creator non approva la richiesta. ` +
        `Se la rifiuta, ti vengono restituiti integralmente.`,
      );
      if (!ok) return;
    }
    setAccessBusy(true);
    try {
      await api.post(`/users/${profile.username}/profile-access/request`);
      toast({
        title: 'Richiesta inviata',
        description: accessPrice > 0
          ? `${accessPrice} Crediti in garanzia fino all'approvazione del creator.`
          : "Attendi l'approvazione del creator.",
      });
      setAccessInfo((prev) => ({ ...prev, my_status: 'pending' }));
    } catch (e) {
      toast({
        title: 'Richiesta non riuscita',
        description: String(e.response?.data?.detail || '').split('|').pop(),
        variant: 'destructive',
      });
    } finally {
      setAccessBusy(false);
    }
  };

  const handleCancelRequest = async () => {
    if (accessBusy) return;
    setAccessBusy(true);
    try {
      const r = await api.post(`/users/${profile.username}/profile-access/cancel`);
      toast({
        title: 'Richiesta annullata',
        description: r.data?.refunded > 0 ? `${r.data.refunded} Crediti restituiti al tuo wallet.` : undefined,
      });
      setAccessInfo((prev) => ({ ...prev, my_status: 'none' }));
    } catch (e) {
      toast({
        title: 'Operazione non riuscita',
        description: String(e.response?.data?.detail || '').split('|').pop(),
        variant: 'destructive',
      });
    } finally {
      setAccessBusy(false);
    }
  };

  // Phase F5 — Voice-First Vetrina replaces the legacy social profile when
  // the Voice-First flag is ON. The legacy layout below stays for rollback.
  if (peopleFeedEnabled) {
    return (
      <VetrinaProfile
        profile={profile}
        setProfile={setProfile}
        me={me}
        posts={posts}
        lives={lives}
        locked={locked}
        accessInfo={accessInfo}
        accessBusy={accessBusy}
        onRequestAccess={handleRequestAccess}
        onCancelRequest={handleCancelRequest}
        blockStatus={blockStatus}
        onToggleBlock={handleToggleBlock}
        blockBusy={blockBusy}
      />
    );
  }

  return (
    <div className="mx-auto max-w-[935px] px-4 pt-6 md:pt-10">
      <BackButton className="mb-4" />
      <div className="flex flex-col items-start gap-6 md:flex-row md:items-center md:gap-12">
        <div className="flex w-full justify-center md:w-auto">
          <div className="relative">
            <Avatar src={profile.avatar_url} alt={profile.username} size={150} />
            {profile.audio_avatar_url && (
              <AudioAvatarPlayer src={profile.audio_avatar_url} size="md" className="absolute bottom-1 right-1" />
            )}
          </div>
        </div>
        <div className="flex w-full flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="flex items-center gap-1.5 text-xl font-light">
              {profile.username}
              {profile.is_verified_creator && <VerifiedBadge size={18} />}
              {profile.is_private && (
                <Lock className="h-4 w-4 gold-text" data-testid="profile-private-lock-icon" />
              )}
              {profile.is_private && accessInfo?.my_status === 'approved' && (
                <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider gold-text" data-testid="profile-premium-badge">
                  Membro
                </span>
              )}
            </h1>
            <div className="flex gap-2">
              {/* Phase F2 · a private profile can be favorited without gaining access */}
              {locked && (
                <Button
                  onClick={async () => {
                    try {
                      const res = await toggleFollow(profile.username);
                      setProfile({ ...profile, is_following: res.following });
                    } catch (e) {
                      toast({ title: 'Operazione non riuscita', variant: 'destructive' });
                    }
                  }}
                  variant="secondary"
                  className="h-8 rounded-md px-3 text-sm font-semibold"
                  data-testid="profile-locked-fav-btn"
                >
                  {profile.is_following ? '♥ Nei Preferiti' : '♡ Preferiti'}
                </Button>
              )}
              {locked ? (
                accessInfo?.my_status === 'pending' ? (
                  <Button
                    onClick={handleCancelRequest}
                    disabled={accessBusy}
                    variant="secondary"
                    className="h-8 rounded-md px-4 text-sm font-semibold"
                    data-testid="profile-cancel-access-btn"
                  >
                    In attesa · Annulla
                  </Button>
                ) : (
                  <Button
                    onClick={handleRequestAccess}
                    disabled={accessBusy}
                    className="gold-gradient h-8 rounded-md px-4 text-sm font-semibold text-black"
                    data-testid="profile-request-access-btn"
                  >
                    <Lock className="mr-1.5 h-3.5 w-3.5" />
                    Richiedi accesso{accessPrice > 0 ? ` · ${accessPrice} cr` : ''}
                  </Button>
                )
              ) : (              <Button
                onClick={async () => {
                  try {
                    const res = await toggleFollow(profile.username);
                    setProfile({
                      ...profile,
                      is_following: res.following,
                    });
                  } catch (e) {
                    toast({ title: 'Could not update follow', variant: 'destructive' });
                  }
                }}
                variant="secondary"
                className={`h-8 rounded-md px-4 text-sm font-semibold ${
                  profile.is_following
                    ? 'bg-secondary text-foreground hover:bg-muted'
                    : 'gold-gradient'
                }`}
              >
                {profile.is_following ? '♥ Nei Preferiti' : '♡ Preferiti'}
              </Button>
              )}
              <Button
                variant="secondary"
                onClick={() => navigate(`/messages?u=${encodeURIComponent(profile.username)}`)}
                className="h-8 rounded-md bg-secondary px-4 text-sm font-semibold text-foreground hover:bg-muted"
                data-testid="profile-message-btn"
              >
                Message
              </Button>
              {/* Phase 54 · "Prenota Live" — SOLO live programmate dal creator
                  (flusso separato dal One-to-One istantaneo). */}
              {scheduledLives.length > 0 && !blockStatus.i_blocked_them && (
                <Button
                  onClick={() => {
                    setTab('lives');
                    setTimeout(() => {
                      const el = document.querySelector('[data-testid="user-profile-lives-upcoming"]');
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 80);
                  }}
                  variant="secondary"
                  className="h-8 rounded-md px-4 text-sm font-semibold"
                  data-testid="profile-book-live-cta"
                >
                  <CalendarClock className="mr-1.5 h-3.5 w-3.5" /> Prenota Live · {scheduledLives.length}
                </Button>
              )}
              {/* Phase 54 · "Live Now" — visibile solo se il creator è in diretta. */}
              {liveNow && !blockStatus.i_blocked_them && (
                <Button
                  onClick={() => navigate(`/lives/${liveNow.id}`)}
                  className="h-8 rounded-md bg-red-500 px-4 text-sm font-semibold text-white hover:bg-red-600"
                  data-testid="profile-live-now-cta"
                >
                  <Radio className="mr-1.5 h-3.5 w-3.5 animate-pulse" /> Live Now
                </Button>
              )}
              {/* Phase 27.8 — Block / Unblock action menu. */}
              <div className="relative">
                <Button
                  variant="ghost"
                  onClick={() => setShowActions((s) => !s)}
                  className="h-8 w-8 rounded-md p-0 text-foreground hover:bg-muted"
                  aria-label="Altre azioni"
                  data-testid="profile-actions-btn"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
                {showActions && (
                  <div
                    className="absolute right-0 top-9 z-20 w-56 overflow-hidden rounded-md border border-border bg-card shadow-xl"
                    data-testid="profile-actions-menu"
                  >
                    <ProfileShareMenuItems username={profile.username} testPrefix="profile" onAction={() => setShowActions(false)} />
                    <button
                      onClick={handleToggleBlock}
                      disabled={blockBusy}
                      className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-semibold hover:bg-muted disabled:opacity-50 ${
                        blockStatus.i_blocked_them ? 'text-emerald-600' : 'text-red-600'
                      }`}
                      data-testid={blockStatus.i_blocked_them ? 'profile-unblock-btn' : 'profile-block-btn'}
                    >
                      {blockStatus.i_blocked_them
                        ? (<><ShieldOff className="h-4 w-4" /> Sblocca @{profile.username}</>)
                        : (<><Shield className="h-4 w-4" /> Blocca @{profile.username}</>)}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Phase 54 · Chiamata One-to-One istantanea — riga separata sotto
              Follow/Message, distinta da "Prenota Live" e "Live Now". */}
          {!blockStatus.i_blocked_them && profile?.id && (
            <CallCreatorSection creatorId={profile.id} username={profile.username} />
          )}
          <div className="flex items-center gap-8">
            <Stat value={profile.posts_count} label="posts" testId="uprofile-stat-posts" />
            {!peopleFeedEnabled && (
              <>
                <Stat value={profile.followers_count} label="followers" onClick={() => setFollowDlg('followers')} testId="uprofile-stat-followers" />
                <Stat value={profile.following_count} label="following" onClick={() => setFollowDlg('following')} testId="uprofile-stat-following" />
              </>
            )}
          </div>
          <div className="text-sm">
            {!peopleFeedEnabled && <p className="font-semibold">{profile.full_name}</p>}
            {profile.bio && <p className="whitespace-pre-wrap" data-testid="profile-bio">{profile.bio}</p>}
            {profile.website && (
              <a
                href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`}
                target="_blank"
                rel="noreferrer"
                className="font-semibold gold-text hover:underline"
              >
                {profile.website}
              </a>
            )}
          </div>
          {/* Phase 55 · Creator info card — chi è, di cosa parla, come chiamarlo */}
          {(profile.topics?.length > 0 || profile.languages?.length > 0 || profile.o2o_mode || profile.created_at) && (
            <div className="flex flex-wrap items-center gap-1.5 pt-1" data-testid="creator-info-card">
              {(profile.topics || []).map((topic) => (
                <span key={topic} className="rounded-full border border-amber-500/30 bg-amber-500/5 px-2.5 py-1 text-[11px] font-semibold gold-text" data-testid="profile-topic-chip">
                  {topic}
                </span>
              ))}
              {profile.languages?.length > 0 && (
                <span className="rounded-full bg-secondary px-2.5 py-1 text-[11px] font-semibold" data-testid="profile-languages" title={profile.languages.map(langLabel).join(', ')}>
                  {profile.languages.map(langFlag).join(' ')}
                </span>
              )}
              {profile.o2o_mode && profile.o2o_rate_credits_per_min > 0 && (
                <span className="rounded-full bg-secondary px-2.5 py-1 text-[11px] font-semibold" data-testid="profile-call-mode">
                  {MODE_LABELS[profile.o2o_mode]}
                </span>
              )}
              {fmtAvgDuration(profile.o2o_avg_call_seconds) && (
                <span className="rounded-full bg-secondary px-2.5 py-1 text-[11px] font-semibold text-muted-foreground" data-testid="profile-avg-duration">
                  ⏱ Conversazioni {fmtAvgDuration(profile.o2o_avg_call_seconds)}
                </span>
              )}
              {profile.o2o_last_available_at && profile.o2o_state !== 'available' && (
                <span className="rounded-full bg-secondary px-2.5 py-1 text-[11px] text-muted-foreground" data-testid="profile-last-available">
                  Ultima disponibilità: {fmtRelativeTime(profile.o2o_last_available_at)}
                </span>
              )}
              {profile.created_at && (
                <span className="rounded-full bg-secondary px-2.5 py-1 text-[11px] text-muted-foreground" data-testid="profile-member-since">
                  Membro da {fmtMemberSince(profile.created_at)}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {locked ? (
        <div className="mt-10 border-t border-border pb-16 pt-12 text-center" data-testid="private-profile-lock">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border-2 border-amber-400">
            <Lock className="h-7 w-7 gold-text" strokeWidth={1.6} />
          </div>
          <h3 className="text-xl font-bold">Questo profilo è privato</h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            Solo i membri approvati da @{profile.username} possono vedere post, live e contenuti.
          </p>
          {accessPrice > 0 && (
            <p className="mt-3 text-sm font-semibold gold-text" data-testid="private-profile-lock-price">
              Accesso: {accessPrice} Crediti (pagamento una tantum)
            </p>
          )}
          <div className="mt-6">
            {accessInfo?.my_status === 'pending' ? (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-amber-300" data-testid="private-profile-pending-note">
                  Richiesta inviata — in attesa di approvazione
                </p>
                <Button
                  onClick={handleCancelRequest}
                  disabled={accessBusy}
                  variant="secondary"
                  data-testid="private-profile-cancel-btn"
                >
                  Annulla richiesta{accessPrice > 0 ? ' (crediti restituiti)' : ''}
                </Button>
              </div>
            ) : (
              <Button
                onClick={handleRequestAccess}
                disabled={accessBusy}
                className="gold-gradient rounded-full px-6 font-bold text-black"
                data-testid="private-profile-request-btn"
              >
                <Lock className="mr-1.5 h-4 w-4" />
                Richiedi accesso{accessPrice > 0 ? ` · ${accessPrice} Crediti` : ''}
              </Button>
            )}
          </div>
        </div>
      ) : (
      <div className="mt-10 border-t border-border pt-4">
        <div className="flex justify-center gap-8 text-xs sm:gap-12">
          <button
            onClick={() => setTab('posts')}
            className={`flex items-center gap-1.5 py-3 uppercase tracking-wider ${
              tab === 'posts' ? 'border-t border-black text-foreground -mt-px font-semibold' : 'text-muted-foreground'
            }`}
          >
            <Grid3x3 className="h-3.5 w-3.5" /> Posts
          </button>
          <button
            onClick={() => setTab('lives')}
            className={`flex items-center gap-1.5 py-3 uppercase tracking-wider ${
              tab === 'lives' ? 'border-t border-black text-foreground -mt-px font-semibold' : 'text-muted-foreground'
            }`}
          >
            <Radio className="h-3.5 w-3.5" /> Lives {upcomingLives.length > 0 && `(${upcomingLives.length})`}
          </button>
          <button
            onClick={() => setTab('tagged')}
            className={`flex items-center gap-1.5 py-3 uppercase tracking-wider ${
              tab === 'tagged' ? 'border-t border-black text-foreground -mt-px font-semibold' : 'text-muted-foreground'
            }`}
          >
            <UserSquare2 className="h-3.5 w-3.5" /> Tagged
          </button>
        </div>

        {loadingTab ? (
          <div className="flex justify-center py-14">
            <div className="h-7 w-7 animate-spin rounded-full border-2 border-border border-t-neutral-800" />
          </div>
        ) : tab === 'posts' ? (
          <div className="pb-16">
            {posts.length === 0 ? (
              <div className="py-16 text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border-2 border-black">
                  <Camera className="h-8 w-8" strokeWidth={1.4} />
                </div>
                <h3 className="text-2xl font-bold">No posts yet</h3>
                <p className="mt-2 text-sm text-foreground">
                  When @{profile.username} shares posts, they will appear here.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-1 pt-4 md:gap-2">
                {posts.map((p) => (
                  <PostThumb
                    key={p.id}
                    post={p}
                    onClick={() => {
                      // For paid+locked posts, scroll user to home feed (the locked card has unlock button)
                      // For unlocked, just keep them here. Simplest: navigate to home for now.
                      if (p.is_paid && !p.has_access) navigate('/');
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        ) : tab === 'lives' ? (
          <div className="pb-16 pt-4">
            {upcomingLives.length === 0 ? (
              <div className="py-16 text-center" data-testid="user-profile-no-lives">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border-2 border-red-200">
                  <Radio className="h-8 w-8 text-red-500" strokeWidth={1.6} />
                </div>
                <h3 className="text-2xl font-bold">Nessuna live in programma</h3>
                <p className="mt-2 text-sm text-foreground">
                  @{profile.username} non ha ancora programmato nessuna live.
                </p>
                {pastLives.length > 0 && (
                  <p className="mt-4 text-xs text-muted-foreground">
                    {pastLives.length} {pastLives.length === 1 ? 'live conclusa' : 'live concluse'} disponibili nello storico in fondo.
                  </p>
                )}
              </div>
            ) : (
              <section data-testid="user-profile-lives-upcoming">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                    <Radio className="h-4 w-4 text-red-500" /> Live in programma
                  </h3>
                  <span className="text-[11px] text-muted-foreground">
                    {upcomingLives.length} {upcomingLives.length === 1 ? 'evento' : 'eventi'}
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  {upcomingLives.map((l) => (
                    <LiveCard
                      key={l.id}
                      live={l}
                      actionable
                      onChange={(updated) => {
                        setLives((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
                      }}
                    />
                  ))}
                </div>
              </section>
            )}
            {pastLives.length > 0 && (
              <section className="mt-8 border-t border-border pt-6" data-testid="user-profile-lives-past">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Storico — Live concluse ({pastLives.length})
                </h3>
                <div className="flex flex-col gap-2 opacity-90">
                  {pastLives.map((l) => (
                    <LiveCard key={l.id} live={l} compact />
                  ))}
                </div>
              </section>
            )}
          </div>
        ) : (
          <div className="py-16 text-center text-sm text-muted-foreground">
            <Bookmark className="mx-auto mb-2 h-6 w-6" />
            Photos of @{profile.username} will appear here
          </div>
        )}
      </div>
      )}

      {/* NeoUI — followers / following lists */}
      <FollowListDialog
        open={!!followDlg}
        onOpenChange={(o) => !o && setFollowDlg(null)}
        username={profile.username}
        type={followDlg || 'followers'}
      />
    </div>
  );
};

export default UserProfilePage;
